import React, { useState } from "react";
import { Alert, Button, Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next"; // Import useTranslation

import DayView from "./DayView";
import MonthView from "./MonthView";
import WeekView from "./WeekView";
import YearView from "./YearView";
import { generateMonthlyMockData } from "../../utils/helperFunctions";

const StatisticsComponent = ({
  expensesItems = [{ total: 0, category: "Gas", purchasedDate: "2024-08-23", expenseId: "" }],
}) => {
  const { t } = useTranslation(); // Initialize useTranslation hook
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("month");

  if (expensesItems.length === 0) {
    expensesItems = generateMonthlyMockData(100);
  }

  const [{ expenseId = "" } = {}] = expensesItems;
  const isMockData = expenseId.includes("MOCK_ID");

  // Navigation handlers object
  const navigationHandlers = {
    day: {
      previous: () => setCurrentDate((prev) => new Date(prev.setDate(prev.getDate() - 1))),
      next: () => setCurrentDate((prev) => new Date(prev.setDate(prev.getDate() + 1))),
    },
    week: {
      previous: () => setCurrentDate((prev) => new Date(prev.setDate(prev.getDate() - 7))),
      next: () => setCurrentDate((prev) => new Date(prev.setDate(prev.getDate() + 7))),
    },
    month: {
      previous: () => setCurrentDate((prev) => new Date(prev.setMonth(prev.getMonth() - 1))),
      next: () => setCurrentDate((prev) => new Date(prev.setMonth(prev.getMonth() + 1))),
    },
    year: {
      previous: () => setCurrentDate((prev) => new Date(prev.setFullYear(prev.getFullYear() - 1))),
      next: () => setCurrentDate((prev) => new Date(prev.setFullYear(prev.getFullYear() + 1))),
    },
  };

  // View components object
  const viewComponents = {
    day: <DayView date={currentDate} expensesItems={expensesItems} />,
    week: (
      <WeekView date={currentDate} setViewMode={setViewMode} setCurrentDate={setCurrentDate} expensesItems={expensesItems} />
    ),
    month: (
      <MonthView
        date={currentDate}
        setViewMode={setViewMode}
        setCurrentDate={setCurrentDate}
        expensesItems={expensesItems}
      />
    ),
    year: (
      <YearView date={currentDate} expensesItems={expensesItems} setViewMode={setViewMode} setCurrentDate={setCurrentDate} />
    ),
  };

  const viewButtonStyle = (isActive) => ({
    backgroundColor: isActive ? "#6EC3C4" : "white",
    color: isActive ? "white" : "#6EC3C4",
    border: `2px solid #6EC3C4`,
    borderRadius: "15px",
    fontWeight: 600,
  });

  return (
    <Container fluid className="mb-4 p-3 rounded-3" style={{ backgroundColor: "#f8f9fa" }}>
      <Row className="align-items-center mb-3">
        <Col md={4}>
          <h3 className="mb-0">🗓️ {currentDate.toLocaleDateString("en-US", t("statistics.dateFormat"))}</h3>
        </Col>
        <Col md={8} className="d-flex gap-2 justify-content-end">
          {["day", "week", "month", "year"].map((mode) => (
            <Button key={mode} style={viewButtonStyle(viewMode === mode)} onClick={() => setViewMode(mode)}>
              {t(`statistics.${mode}View`)}
            </Button>
          ))}
        </Col>
      </Row>

      {isMockData && (
        <Row className="mb-3">
          <Col>
            <Alert variant="info">{t("statistics.mockDataAlert")}</Alert>
          </Col>
        </Row>
      )}

      <Row className="mb-3">
        <Col className="d-flex gap-2 justify-content-center">
          <Button variant="outline-primary" className="rounded-pill" onClick={navigationHandlers[viewMode].previous}>
            ⏪ {t("statistics.previous")}
          </Button>
          <Button variant="outline-primary" className="rounded-pill" onClick={navigationHandlers[viewMode].next}>
            {t("statistics.next")} ⏩
          </Button>
        </Col>
      </Row>

      <Row>
        <Col>
          <div className="p-3 rounded-3" style={{ backgroundColor: "white" }}>
            {viewComponents[viewMode]}
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default StatisticsComponent;
